import type { ClientId } from '../../core/ClientId';
import type { PortCallData } from '../../core/Core';
import type { ScenarioLocation } from '../../../lib/planningData';
import type {
  CalculateVoyagesQueryVariables,
  CalculateVoyagesQuery,
  Currency,
  ScenarioTimeline_ShipFragment,
  VesonDefaultBunkerPrice,
  VesonRemainingBunkerOfVoyage,
} from '../../../graphql';
import {
  BunkerGrade,
  CalculateVoyagesDocument,
  VesselArea,
  VesselSpeedTag,
  VesselSpeedType,
} from '../../../graphql';
import { useApolloClient, type ApolloClient } from '@apollo/client';
import {
  getCostSharingDetailsForPortCalls,
  type CostSharingDetailsByPortCall,
} from '../TCE';
import type { Cost, CostsByPortCall } from './costs';
import { getVoyageCalculationInputs } from '../ShippingPlan';
import {
  isCargoEventTimeData,
  isPortCallEventTimeData,
  type EnrichedSpeedConsumption,
  type Leg,
} from '../../core/TimeData';
import { assertDefined, isDefined } from '../../../lib/util';
import {
  getImosDefaultRobPriceByGrade,
  getImosRobByGrade,
  getShipmentVesonRobDetails,
  type VesonBunker,
} from '../../integrations/VesonRob';
import { hasFeature, useDefaultCurrency } from '../../../state/zustand';
import { useBunkerPrices } from '../../hooks/useBunkerPrices';
import { useCallback } from 'react';
import { type EventTimeline } from '../../core/Timelines';
import { getTimelineBounds } from '../timelineBounds';
import { getTimelinePortCalls } from '../shipment';

export type BunkerConsumption = {
  type: 'BALLAST' | 'LADEN' | 'PORT';
  bunkerGrade: string;
  consumptionTonnes: number;
};

export type BunkerConsumptionByPortCall = Map<
  PortCallData['id'],
  BunkerConsumption[]
>;

const fallbackMGOPrice = 600;
const fallbackLSFOPrice = 500;

export const useBunkerCosts = (
  focusedShipments: Set<ClientId>,
  vesonBunkerData?: VesonBunker,
) => {
  const apolloClient = useApolloClient();
  const baseCurrency = useDefaultCurrency();

  const isVoyageCalculationAvailable = hasFeature('calculate_voyage');
  const { bunkerPriceByGradeName } = useBunkerPrices();
  const getTimelineBunkerCosts = useCallback(
    async (
      timeline: EventTimeline,
      focusedOrderIds: Set<number> | null,
    ): Promise<{
      bunkerCosts: CostsByPortCall;
      bunkerConsumptionByPortCall: BunkerConsumptionByPortCall;
    } | null> => {
      if (timeline.type !== 'tc' || !timeline.vessel) {
        return null;
      }

      const costSharingDetailsByPortCall = getCostSharingDetailsForPortCalls(
        timeline.parent?.voyageGroups.flatMap((vg) => vg.events) ??
          timeline.voyageGroups.flatMap((vg) => vg.events),
      );

      const timeBounds = getTimelineBounds(timeline);
      const { legs, locationById, portCalls } = getTimelinePortCalls(timeline);

      const legsWithoutInitialLaden = legs.filter(
        (leg, index) => index !== 0 || !leg.laden,
      );

      const voyageBunker = vesonBunkerData
        ? getShipmentVesonRobDetails(timeline, focusedOrderIds, vesonBunkerData)
        : undefined;

      const { bunkerConsumptionByPortCall, costsByPortCall } =
        await getBunkerCosts(
          focusedOrderIds,
          focusedShipments,
          timeBounds,
          portCalls,
          locationById,
          timeline.vessel,
          bunkerPriceByGradeName,
          legsWithoutInitialLaden,
          apolloClient,
          isVoyageCalculationAvailable,
          baseCurrency,
          costSharingDetailsByPortCall,
          voyageBunker,
        );

      return { bunkerCosts: costsByPortCall, bunkerConsumptionByPortCall };
    },
    [
      apolloClient,
      baseCurrency,
      bunkerPriceByGradeName,
      isVoyageCalculationAvailable,
      vesonBunkerData,
      focusedShipments,
    ],
  );

  return { getTimelineBunkerCosts };
};

export const getBunkerCosts = async (
  /** If defined, filter costs to only include the ones associated with these orders */
  focusedOrderIds: Set<number> | null,
  focusedShipmentIds: Set<ClientId>,
  timeBounds: {
    startTime: number | null;
    endTime: number | null;
  },
  portCalls: PortCallData[],
  locationById: Map<number, ScenarioLocation>,
  vessel: ScenarioTimeline_ShipFragment,
  bunkerPriceByGradeName: (gradeName: string) => number | null,
  legs: Leg[],
  client: ApolloClient<unknown>,
  isVoyageCalculationAvailable: boolean,
  baseCurrency: Currency,
  costSharingDetailsByPortCall: CostSharingDetailsByPortCall,
  voyageBunker?: {
    remainingBunkerOfVoyage?: VesonRemainingBunkerOfVoyage;
    remainingBunkerOfPreVoyage?: VesonRemainingBunkerOfVoyage;
    defaultBunker?: VesonDefaultBunkerPrice[];
  },
): Promise<{
  bunkerConsumptionByPortCall: BunkerConsumptionByPortCall;
  costsByPortCall: CostsByPortCall;
}> => {
  const bunkerConsumptionByPortCall: BunkerConsumptionByPortCall = new Map();

  const costsByPortCall: CostsByPortCall = new Map();

  const bunkerPricePerTonne = bunkerPriceByGradeName('MGO'); // market price

  const inputs = getVoyageCalculationInputs(
    vessel,
    legs,
    bunkerPriceByGradeName,
  );

  const voyageInputs = Array.from(inputs.values());

  const calculateVoyageResponse =
    isVoyageCalculationAvailable && voyageInputs.length
      ? await client.query<
          CalculateVoyagesQuery,
          CalculateVoyagesQueryVariables
        >({
          query: CalculateVoyagesDocument,
          variables: {
            inputs: voyageInputs,
          },
        })
      : null;
  const voyageCalculations = calculateVoyageResponse?.data.calculateVoyages;

  const imosRobInventory =
    voyageBunker?.remainingBunkerOfVoyage?.bunkerOpen ??
    voyageBunker?.remainingBunkerOfPreVoyage?.bunkerClose ??
    null;

  const robByGrade = imosRobInventory
    ? getImosRobByGrade(imosRobInventory)
    : undefined;

  const defaultRobByGradeName = voyageBunker?.defaultBunker
    ? getImosDefaultRobPriceByGrade(voyageBunker.defaultBunker)
    : undefined;

  for (const [index, pc] of portCalls.entries()) {
    const location = locationById.get(pc.locationId);
    const portCallEntry = pc?.tds.find(isPortCallEventTimeData)?.event.entry;

    const firstTd = pc.tds.at(0);
    if (!firstTd) continue;

    const area = firstTd.area;

    const isInitialLadenLeg = index === 0 && firstTd.laden;

    const legIndex = legs.findIndex((leg) => leg.event.portCallId === pc.id);
    const fuelConsumptionBreakdown =
      voyageCalculations?.[legIndex]?.fuelConsumptionBreakdown?.value;

    const portCallCosts: Cost[] = [];
    const portCallConsumption: BunkerConsumption[] = [];

    assertDefined(pc.id, 'Port call id is expected to be defined');
    const costSharingDetails = costSharingDetailsByPortCall.get(pc.id);

    /**
     * Leg costs should be ignored if focused shipments is defined and the
     * earliest loaded shipment is not included in the focused shipments
     */
    const ignoreLegCosts =
      !!costSharingDetails?.firstShipmentOnBoard &&
      focusedShipmentIds.size > 0 &&
      !focusedShipmentIds.has(costSharingDetails.firstShipmentOnBoard);

    /*
     * Leg consumption
     */
    if (!ignoreLegCosts && !isInitialLadenLeg && fuelConsumptionBreakdown) {
      const insideSeca = fuelConsumptionBreakdown.MGO;
      const outsideSeca = fuelConsumptionBreakdown.LSFO;
      if (insideSeca) {
        const insideSecaConsumptionByTon = insideSeca.consumption / 1000;
        const insideSecaRob = robByGrade?.get('MGO');
        const insideSecaDefaultRob = defaultRobByGradeName?.get('MGO');
        let insideSecaRobValue = 0;
        if (insideSecaRob) {
          let neededBunkerMore = insideSecaConsumptionByTon;
          for (let i = insideSecaRob.length - 1; i >= 1; i--) {
            const { amount: amountInTon, price: pricePerTon } =
              insideSecaRob[i];
            if (neededBunkerMore >= amountInTon) {
              neededBunkerMore -= amountInTon;
              insideSecaRobValue += amountInTon * pricePerTon;
              insideSecaRob.splice(i, 1);
            } else {
              insideSecaRob[i].amount -= neededBunkerMore;
              insideSecaRobValue += neededBunkerMore * pricePerTon;
              neededBunkerMore = 0;
              break;
            }
          }
          if (neededBunkerMore > 0) {
            insideSecaRobValue +=
              neededBunkerMore * (insideSecaRob[0].price ?? fallbackMGOPrice);
          }
        } else if (insideSecaDefaultRob) {
          insideSecaRobValue +=
            insideSecaDefaultRob * insideSecaConsumptionByTon;
        }

        portCallConsumption.push({
          type: firstTd.laden ? 'LADEN' : 'BALLAST',
          bunkerGrade: BunkerGrade.MGO,
          consumptionTonnes: insideSeca.consumption,
        });

        portCallCosts.push({
          type: 'BUNKER',
          name: `Bunker cost inside SECA ${
            firstTd.laden ? 'laden' : 'ballast'
          }`,
          value:
            insideSecaRob || insideSecaDefaultRob
              ? insideSecaRobValue
              : insideSeca.cost,
          numShipments: 1,
          params: [
            {
              testId: 'inside-SECA-voyage-duration-h',
              name: 'Voyage duration (h)',
              value: firstTd.hoursAtSea,
              source: 'SEABER',
            },
            {
              testId: 'inside-SECA-consumption-t',
              name: 'Bunker consumption (t)',
              value: insideSecaConsumptionByTon,
              source: 'NAPA',
            },
            {
              testId: 'inside-SECA-bunker-price',
              name:
                'Bunker price MGO ' + insideSecaRob
                  ? insideSecaRobValue / insideSecaConsumptionByTon +
                    ` (${baseCurrency}/t)`
                  : bunkerPricePerTonne
                    ? `(${baseCurrency}/t)`
                    : '(USD/t)',
              value: insideSecaRob
                ? insideSecaRobValue / insideSecaConsumptionByTon
                : (bunkerPricePerTonne ?? fallbackMGOPrice),
              source: 'MANUAL',
            },
          ],
          locations: location ? [location] : [],
          portCallEntry: portCallEntry ?? null,
        });
      }
      if (outsideSeca) {
        portCallConsumption.push({
          type: firstTd.laden ? 'LADEN' : 'BALLAST',
          bunkerGrade: BunkerGrade.ULSFO,
          consumptionTonnes: outsideSeca.consumption,
        });
        const outsideSecaConsumptionByTon = outsideSeca.consumption / 1000;
        const outsideSecaRob = robByGrade?.get('LSF');
        const outsideSecaDefaultRob = defaultRobByGradeName?.get('LSF');
        let outsideSecaRobValue = 0;
        if (outsideSecaRob) {
          let neededBunkerMore = outsideSecaConsumptionByTon;
          for (let i = outsideSecaRob.length - 1; i >= 1; i--) {
            const { amount: amountInTon, price: pricePerTon } =
              outsideSecaRob[i];
            if (neededBunkerMore >= amountInTon) {
              neededBunkerMore -= amountInTon;
              outsideSecaRobValue += amountInTon * pricePerTon;
              outsideSecaRob.splice(i, 1);
            } else {
              outsideSecaRob[i].amount -= neededBunkerMore;
              outsideSecaRobValue += neededBunkerMore * pricePerTon;
              neededBunkerMore = 0;
              break;
            }
          }
          if (neededBunkerMore > 0) {
            outsideSecaRobValue +=
              neededBunkerMore * (outsideSecaRob[0].price ?? fallbackLSFOPrice);
          }
        } else if (outsideSecaDefaultRob) {
          outsideSecaRobValue +=
            outsideSecaDefaultRob * outsideSecaConsumptionByTon;
        }

        portCallCosts.push({
          type: 'BUNKER',
          name: `Bunker cost outside SECA ${
            firstTd.laden ? 'laden' : 'ballast'
          }`,
          value:
            outsideSecaRob || outsideSecaDefaultRob
              ? outsideSecaRobValue
              : outsideSeca.cost,
          numShipments: 1,
          params: [
            {
              testId: 'outside-SECA-voyage-duration-h',
              name: 'Voyage duration (h)',
              value: firstTd.hoursAtSea,
              source: 'SEABER',
            },
            {
              testId: 'outside-SECA-consumption-t',
              name: 'Bunker consumption (t)',
              value: outsideSecaConsumptionByTon,
              source: 'NAPA',
            },
            {
              testId: 'outside-SECA-bunker-price',
              name:
                'Bunker price ' +
                (bunkerPricePerTonne
                  ? `MGO (${baseCurrency}/t)`
                  : 'LSFO (USD/t)'),
              value: outsideSecaRob
                ? outsideSecaRobValue / outsideSecaConsumptionByTon
                : (bunkerPricePerTonne ?? fallbackLSFOPrice),
              source: 'MANUAL',
            },
          ],
          locations: location ? [location] : [],
          portCallEntry: portCallEntry ?? null,
        });
      }
    } else if (!ignoreLegCosts && !isInitialLadenLeg) {
      const { insideEcaData, outsideEcaData } = firstTd.costs?.bunkerGrade;

      const insideEcaRob = robByGrade?.get(insideEcaData?.gradeName ?? '');
      const insideEcaDefaultRob = defaultRobByGradeName?.get(
        insideEcaData?.gradeName ?? '',
      );

      let insideEcaRobValue = 0;
      if (insideEcaRob && insideEcaData) {
        let neededBunkerMore = insideEcaData.consumedBunker;

        for (let i = insideEcaRob.length - 1; i >= 1; i--) {
          const { amount: amountInTon, price: pricePerTon } = insideEcaRob[i];
          if (neededBunkerMore >= amountInTon) {
            neededBunkerMore -= amountInTon;
            insideEcaRobValue += amountInTon * pricePerTon;
            insideEcaRob.splice(i, 1);
          } else {
            insideEcaRob[i].amount -= neededBunkerMore;
            insideEcaRobValue += neededBunkerMore * pricePerTon;
            neededBunkerMore = 0;
            break;
          }
        }
        // If not enough fuel, calculate the missing fuel with market price
        if (neededBunkerMore > 0) {
          insideEcaRobValue += neededBunkerMore * insideEcaRob[0].price;
        }
      } else if (insideEcaDefaultRob && insideEcaData) {
        insideEcaRobValue += insideEcaData.consumedBunker * insideEcaDefaultRob;
      }

      const outsideEcaRob = robByGrade?.get(outsideEcaData?.gradeName ?? '');
      const outsideEcaDefaultRob = defaultRobByGradeName?.get(
        outsideEcaData?.gradeName ?? '',
      );

      let outsideEcaRobValue = 0;

      if (outsideEcaRob && outsideEcaData) {
        let neededBunkerMore = outsideEcaData.consumedBunker;
        for (let i = outsideEcaRob.length - 1; i >= 1; i--) {
          const { amount: amountInTon, price: pricePerTon } = outsideEcaRob[i];
          if (neededBunkerMore >= amountInTon) {
            neededBunkerMore -= amountInTon;
            outsideEcaRobValue += amountInTon * pricePerTon;
            outsideEcaRob.splice(i, 1);
          } else {
            outsideEcaRob[i].amount -= neededBunkerMore;
            outsideEcaRobValue += neededBunkerMore * pricePerTon;
            neededBunkerMore = 0;
            break;
          }
        }
        if (neededBunkerMore > 0) {
          outsideEcaRobValue += neededBunkerMore * outsideEcaRob[0].price;
        }
      } else if (outsideEcaDefaultRob && outsideEcaData) {
        outsideEcaRobValue +=
          outsideEcaData.consumedBunker * outsideEcaDefaultRob;
      }

      const calcBunkerCost = (
        data: EnrichedSpeedConsumption,
        insideEca: boolean,
      ) => {
        if (ignoreLegCosts) return;
        const bunkerConsumption = data.consumedBunker;
        const bunkerCost = data.voyageCost;
        const duration = data.duration;

        portCallCosts.push({
          type: 'BUNKER',
          name: `Bunker cost ${data.targetArea === VesselArea.ALL_AREAS ? '' : insideEca ? 'inside ECA' : 'outside ECA'} ${
            firstTd.laden ? 'laden' : 'ballast'
          }`,
          value: insideEca
            ? insideEcaRob || insideEcaDefaultRob
              ? insideEcaRobValue
              : bunkerCost
            : outsideEcaRob || outsideEcaDefaultRob
              ? outsideEcaRobValue
              : bunkerCost,
          numShipments: 1,
          params: [
            {
              testId: 'bunker-cost-consumption-t-day',
              name: 'Bunker consumption (t/day)',
              value: data.consumption,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-voyage-duration-h',
              name: 'Voyage duration (h)',
              value: duration,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-consumption',
              name: 'Bunker consumption (t)',
              value: bunkerConsumption,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-grade',
              name: `Bunker grade`,
              value: data.gradeName || data.referenceGrade,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-bunker-price',
              name: `Bunker price (${baseCurrency}/t)`,
              value: data.fuelCost,
              source: 'SEABER',
            },
          ],
          locations: location ? [location] : [],
          portCallEntry: portCallEntry ?? null,
          bunkerGradeParams: {
            area: data.targetArea,
            type: firstTd.laden
              ? VesselSpeedType.LADEN
              : VesselSpeedType.BALLAST,
            tag: null,
          },
        });

        portCallConsumption.push({
          type: firstTd.laden ? 'LADEN' : 'BALLAST',
          bunkerGrade: data.referenceGrade,
          consumptionTonnes: data.consumption,
        });
      };

      if (insideEcaData) {
        calcBunkerCost(
          {
            ...insideEcaData,
            fuelCost:
              insideEcaRob || insideEcaDefaultRob
                ? insideEcaRobValue / insideEcaData.consumedBunker
                : insideEcaData.fuelCost,
          },
          true,
        );
      }
      if (outsideEcaData) {
        calcBunkerCost(
          {
            ...outsideEcaData,
            fuelCost:
              outsideEcaRob || outsideEcaDefaultRob
                ? outsideEcaRobValue / outsideEcaData.consumedBunker
                : outsideEcaData.fuelCost,
          },
          false,
        );
      }
    }

    /*
     * Port consumption
     */
    const portBunkerData = pc.tds.find((td) =>
      isDefined(td.costs.bunkerGrade.portData),
    )?.costs.bunkerGrade.portData;

    const extraConsumptionData = pc.tds.find((td) =>
      isDefined(td.costs.bunkerGrade.extraConsumptionData),
    )?.costs.bunkerGrade.extraConsumptionData;

    if (portBunkerData || extraConsumptionData) {
      const filteredTds = focusedOrderIds
        ? pc.tds.filter((td) => {
            if (isCargoEventTimeData(td)) {
              return focusedOrderIds.has(td.event.order.id);
            }

            return (
              (timeBounds?.startTime ?? 0) <= td.start &&
              td.start < (timeBounds?.endTime ?? Infinity)
            );
          })
        : pc.tds;

      let portBunkerCost = 0;
      let portBunkerConsumption = 0;
      let portCallDuration = 0;

      let extraBunkerCost = 0;
      let extraBunkerConsumption = 0;
      let extraBunkerDuration = 0;

      filteredTds.forEach((td) => {
        portBunkerCost += Number(
          td.costs.bunkerGrade.portData?.voyageCost ?? 0,
        );
        portBunkerConsumption += Number(
          td.costs.bunkerGrade.portData?.consumedBunker ?? 0,
        );
        portCallDuration += Number(
          td.costs.bunkerGrade.portData?.duration ?? 0,
        );

        extraBunkerCost += Number(
          td.costs.bunkerGrade.extraConsumptionData?.voyageCost ?? 0,
        );
        extraBunkerConsumption += Number(
          td.costs.bunkerGrade.extraConsumptionData?.consumedBunker ?? 0,
        );
        extraBunkerDuration += Number(
          td.costs.bunkerGrade.extraConsumptionData?.duration ?? 0,
        );
      });

      if (extraConsumptionData) {
        const extraConsumptionRob = robByGrade?.get(
          extraConsumptionData.gradeName || extraConsumptionData.referenceGrade,
        );
        const extraConsumptionDefaultRob = defaultRobByGradeName?.get(
          extraConsumptionData.gradeName || extraConsumptionData.referenceGrade,
        );

        let extraConsumptionRobValue = 0;
        if (extraConsumptionRob) {
          let neededBunkerMore = extraBunkerConsumption;
          for (let i = extraConsumptionRob.length - 1; i >= 1; i--) {
            const { amount: amountInTon, price: pricePerTon } =
              extraConsumptionRob[i];
            if (neededBunkerMore >= amountInTon) {
              neededBunkerMore -= amountInTon;
              extraConsumptionRobValue += amountInTon * pricePerTon;
              extraConsumptionRob.splice(i, 1);
            } else {
              extraConsumptionRob[i].amount -= neededBunkerMore;
              extraConsumptionRobValue += neededBunkerMore * pricePerTon;
              neededBunkerMore = 0;
              break;
            }
          }
          // If not enough fuel, calculate the missing fuel with market price
          if (neededBunkerMore > 0) {
            extraConsumptionRobValue +=
              neededBunkerMore * extraConsumptionRob[0].price;
          }
        } else if (extraConsumptionDefaultRob) {
          extraConsumptionRobValue +=
            extraBunkerConsumption * extraConsumptionDefaultRob;
        }

        portCallCosts.push({
          type: 'BUNKER',
          name: 'Bunker cost idle',
          value:
            extraConsumptionRob || extraConsumptionDefaultRob
              ? extraConsumptionRobValue
              : extraBunkerCost,
          numShipments: 1,
          params: [
            {
              testId: 'bunker-cost-idle-consumption-t-day',
              name: 'Bunker consumption (t/day)',
              value: extraConsumptionData.consumption,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-idle-duration',
              name: 'Duration (h)',
              value: extraBunkerDuration,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-idle-consumption',
              name: 'Bunker consumption (t)',
              value: extraBunkerConsumption,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-idle-grade',
              name: `Bunker grade`,
              value:
                (extraConsumptionRob || extraConsumptionDefaultRob) &&
                extraConsumptionData.gradeName
                  ? extraConsumptionData.gradeName
                  : extraConsumptionData.referenceGrade,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-idle-bunker-price',
              name: `Bunker price (${baseCurrency}/t)`,
              value:
                extraConsumptionRob || extraConsumptionDefaultRob
                  ? extraConsumptionRobValue / extraBunkerConsumption
                  : extraConsumptionData.fuelCost,
              source: 'SEABER',
            },
          ],
          locations: location ? [location] : [],
          portCallEntry: portCallEntry ?? null,
          bunkerGradeParams: {
            area,
            type: VesselSpeedType.PORT,
            tag: VesselSpeedTag.IDLE,
          },
        });

        portCallConsumption.push({
          type: 'PORT',
          bunkerGrade:
            extraConsumptionRob && extraConsumptionData.gradeName
              ? extraConsumptionData.gradeName
              : extraConsumptionData.referenceGrade,
          consumptionTonnes: extraBunkerConsumption,
        });
      }

      if (portBunkerData) {
        const portBunkerRob = robByGrade?.get(portBunkerData.gradeName || '');
        const portBunkerDefaultRob = defaultRobByGradeName?.get(
          portBunkerData.gradeName || '',
        );
        let portRobValue = 0;
        if (portBunkerRob) {
          let neededBunkerMore = portBunkerConsumption;
          for (let i = portBunkerRob.length - 1; i >= 1; i--) {
            const { amount: amountInTon, price: pricePerTon } =
              portBunkerRob[i];
            if (neededBunkerMore >= amountInTon) {
              neededBunkerMore -= amountInTon;
              portRobValue += amountInTon * pricePerTon;
              portBunkerRob.splice(i, 1);
            } else {
              portBunkerRob[i].amount -= neededBunkerMore;
              portRobValue += neededBunkerMore * pricePerTon;
              neededBunkerMore = 0;
              break;
            }
          }
          if (neededBunkerMore > 0) {
            portRobValue += neededBunkerMore * portBunkerRob[0].price;
          }
        } else if (portBunkerDefaultRob) {
          portRobValue += portBunkerConsumption * portBunkerDefaultRob;
        }
        portCallCosts.push({
          type: 'BUNKER',
          name: 'Bunker cost port',
          value:
            portBunkerRob || portBunkerDefaultRob
              ? portRobValue
              : portBunkerCost,
          numShipments: 1,
          params: [
            {
              testId: 'bunker-cost-port-consumption-t-day',
              name: 'Bunker consumption (t/day)',
              value: portBunkerData.consumption,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-port-duration-h',
              name: 'Port call duration (h)',
              value: portCallDuration,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-port-consumption',
              name: 'Bunker consumption (t)',
              value: portBunkerConsumption,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-port-grade',
              name: `Bunker grade`,
              value:
                (portRobValue || portBunkerDefaultRob) &&
                portBunkerData.gradeName
                  ? portBunkerData.gradeName
                  : portBunkerData.referenceGrade,
              source: 'SEABER',
            },
            {
              testId: 'bunker-cost-port-price',
              name: `Bunker price (${baseCurrency}/t)`,
              value: portRobValue
                ? portRobValue / portBunkerConsumption
                : portBunkerData.fuelCost,
              source: 'SEABER',
            },
          ],
          locations: location ? [location] : [],
          portCallEntry: portCallEntry ?? null,
          bunkerGradeParams: {
            area,
            type: VesselSpeedType.PORT,
            tag: null,
          },
        });

        portCallConsumption.push({
          type: 'PORT',
          bunkerGrade:
            portRobValue && portBunkerData.gradeName
              ? portBunkerData.gradeName
              : portBunkerData.referenceGrade,
          consumptionTonnes: portBunkerConsumption,
        });
      }
    }

    costsByPortCall.set(pc.id, portCallCosts);
    bunkerConsumptionByPortCall.set(pc.id, portCallConsumption);
  }

  return { bunkerConsumptionByPortCall, costsByPortCall };
};
